// System
import { useMemo } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';

// Types
import { RubberDuckCrossedLaneDto } from '@Modal/reducers/rounds/types';

type Props = {
  laneCount?: number;
  failLane?: number;
  reachedLane?: number | null;
  status?: string;
  crossedLanes?: RubberDuckCrossedLaneDto[];
};

type LaneState = 'crossed' | 'failed' | 'pending';

const laneStyles: Record<LaneState, { bg: string; border: string; color: string }> = {
  crossed: { bg: '#e6f4ea', border: '#34a853', color: '#1e7e34' },
  failed: { bg: '#fce8e6', border: '#d93025', color: '#b3261e' },
  pending: { bg: '#f1f3f4', border: '#dadce0', color: '#80868b' },
};

export const RubberDuckLaneStrip: React.FC<Props> = ({
  laneCount,
  failLane,
  reachedLane,
  status,
  crossedLanes,
}) => {
  const multiplierByLane = useMemo(() => {
    const map = new Map<number, number>();
    crossedLanes?.forEach(({ lane, multiplier }) => map.set(lane, multiplier));
    return map;
  }, [crossedLanes]);

  if (!laneCount) return null;

  const reached = reachedLane ?? 0;
  const survivedWholeTrack = failLane === laneCount + 1;

  const laneStateOf = (lane: number): LaneState => {
    if (lane <= reached) return 'crossed';
    if (status === 'LOST' && lane === failLane) return 'failed';
    return 'pending';
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <strong>Lane Track</strong>
        <Typography variant="caption" color="text.secondary">
          {reached} / {laneCount} crossed
          {survivedWholeTrack ? ' — survived whole track' : ''}
        </Typography>
      </Stack>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px',
        }}
      >
        {Array.from({ length: laneCount }, (_, index) => {
          const lane = index + 1;
          const state = laneStateOf(lane);
          const multiplier = multiplierByLane.get(lane);
          const palette = laneStyles[state];

          return (
            <Tooltip
              key={lane}
              title={
                state === 'failed'
                  ? `Lane ${lane} — crushed here`
                  : multiplier !== undefined
                    ? `Lane ${lane} — ${multiplier}x`
                    : `Lane ${lane}`
              }
            >
              <Box
                sx={{
                  width: '44px',
                  minHeight: '44px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  border: `1px solid ${palette.border}`,
                  backgroundColor: palette.bg,
                  color: palette.color,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {lane}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '10px', lineHeight: 1.2 }}>
                  {state === 'failed' ? '✕' : multiplier !== undefined ? `${multiplier}x` : '—'}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Stack>
  );
};
